// components/EditProjectModal.jsx
import { useEffect } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { create, updateProject } from '../services/project/projectService';

const schema = Yup.object({
  name: Yup.string().trim().required('Project name is required').max(100),
});

const EditProjectModal = ({ project, show, onClose, refetch = () => {} }) => {
  const formik = useFormik({
    initialValues: {
      name: project?.name || '',
      validation: project?.settings?.validation ?? false,
      approval: project?.settings?.approval ?? false,
      tagOnly: project?.settings?.tagOnly ?? false,
    },
    enableReinitialize: true,
    validationSchema: schema,
    onSubmit: async ({ name }, { setSubmitting }) => {
      try {
        const payload = {
          name: name.trim(),
          validation: formik.values.validation,
          approval: formik.values.approval,
          tagOnly: formik.values.tagOnly,
        };
        if (project?.id) {
          const { message } = await updateProject(project.id, payload);
          toast.success(message || 'Project updated');
        } else {
          const { message } = await create(payload);
          toast.success(message || 'Project created');
        }
        refetch();
        onClose();
      } catch (error) {
        const msg =
          error?.errorMessage ||
          error?.response?.data?.errorMessage ||
          'Something went wrong';
        toast.error(msg);
      } finally {
        setSubmitting(false);
      }
    },
  });

  useEffect(() => {
    if (show) {
      setTimeout(() => {
        document.getElementById('projectNameInput')?.focus();
      }, 100);
    }
  }, [show]);

  const unchanged =
    (project?.name || '').trim() === (formik.values.name || '').trim() &&
    (project?.validation === formik.values.validation) &&
    (project?.approval === formik.values.approval);

  return (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>{project ? 'Edit Project' : 'New Project'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={formik.handleSubmit}>
          <Form.Group className="mb-3" controlId="projectNameInput">
            <Form.Label>Project Name</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter project name"
              {...formik.getFieldProps('name')}
              isInvalid={formik.touched.name && !!formik.errors.name}
              disabled={formik.isSubmitting}
            />
            <Form.Control.Feedback type="invalid">
              {formik.errors.name}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3" controlId="validationCheckbox">
            <Form.Check
              type="checkbox"
              label="Needs Validation"
              {...formik.getFieldProps('validation')}
              checked={formik.values.validation}
              disabled={formik.isSubmitting}
            />
          </Form.Group>

          <Form.Group className="mb-3" controlId="approvalCheckbox">
            <Form.Check
              type="checkbox"
              label="Needs Approval"
              {...formik.getFieldProps('approval')}
              checked={formik.values.approval}
              disabled={formik.isSubmitting}
            />
          </Form.Group>
          {formik.values.approval && (
            <Form.Group className="mb-3" controlId="tagOnlyCheckbox">
              <Form.Check
                type="checkbox"
                label="Tag Only"
                {...formik.getFieldProps('tagOnly')}
                checked={formik.values.tagOnly}
                disabled={formik.isSubmitting}
              />
            </Form.Group>
          )}

          <div className="d-flex justify-content-end gap-2">
            <Button variant="secondary" onClick={onClose} disabled={formik.isSubmitting}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={formik.isSubmitting || unchanged || !!formik.errors.name}
            >
              {formik.isSubmitting ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default EditProjectModal;
